import { inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Observable, take } from 'rxjs';
import { MessageRole } from '../models/copilot.models';
import { CopilotState } from '../store/copilot.reducer';
import { CopilotActions } from '../store/copilot.actions';
import {
  selectedClient,
  selectedClientId,
  selectedConversationId,
  selectHighlightedClientIds,
  selectHighlightedConversations,
  selectIsStreaming,
  selectMessagesByConversation,
  selectMessagesForSelectedConversation,
} from '../store/copilot.selectors';
import { CopilotApiService } from './copilot.service';

@Injectable({
  providedIn: 'root',
})
export class CopilotFacadeService {
  private _store = inject(Store<CopilotState>);
  private _copilotApiService = inject(CopilotApiService);

  readonly selectedClientId$ = this._store.select(selectedClientId);
  readonly selectedClient$ = this._store.select(selectedClient);
  readonly selectedConversation$ = this._store.select(selectedConversationId);
  private _clearContextSignal = signal(0); // use a number
  readonly clearContextSignal = this._clearContextSignal.asReadonly();

  readonly messagesForSelectedConversation$ = this._store.select(
    selectMessagesForSelectedConversation
  );
  readonly highlightedClientIds$ = this._store.select(
    selectHighlightedClientIds
  );
  readonly highlightedConversations$ = this._store.select(
    selectHighlightedConversations
  );
  isStreaming$(conversationId: string): Observable<boolean> {
    return this._store.select(selectIsStreaming(conversationId));
  }

  disableInput = signal(false);
  hideInput = signal(false);
  //
  // lastSelectedConversationId$(view: 'active' | 'archived') {
  //   return this._store.select(selectLastConversationId(view));
  // }

  selectClient(clientId: string) {
    this._store.dispatch(CopilotActions.selectClient({ clientId }));
    this._store.dispatch(CopilotActions.clearClientHighlight({ clientId }));
  }

  selectConversation(
    clientId: string,
    conversationId: string,
    view?: 'active' | 'archived'
  ) {
    this.selectClient(clientId);
    this._store.dispatch(
      CopilotActions.selectConversation({ clientId, conversationId, view })
    );
  }

  loadConversations(clientId: string) {
    this._store.dispatch(CopilotActions.loadConversations({ clientId }));
  }

  loadMessages(clientId: string, conversationId: string, forceRefresh = false) {
    this._store.dispatch(
      CopilotActions.loadMessages({ clientId, conversationId, forceRefresh })
    );
  }

  sendMessage(
    clientId: string,
    conversationId: string,
    content: string,
    fileName?: string,
    loggedInUserId?: string
  ) {
    const userMsg = {
      clientId,
      conversationId,
      message: {
        id: `temp-user-${Date.now()}`,
        conversation_id: conversationId,
        role: MessageRole.USER,
        content,
        created_at: new Date().toISOString(),
        metadata: {},
        owner_id: loggedInUserId ?? undefined
      },
    };

    this._store.dispatch(CopilotActions.messageReceived(userMsg));
    this._copilotApiService.broadcast('new-message', userMsg);
    // Begin streaming assistant response
    this._store.dispatch(
      CopilotActions.sendMessage({
        clientId,
        conversationId,
        content,
        fileName,
      })
    );
  }

  clearClient(clientId: string) {
    this._store.dispatch(CopilotActions.clearClient({ clientId }));
  }

  markStreamingStart(clientId: string, conversationId: string) {
    this._store.dispatch(
      CopilotActions.startStreaming({ clientId, conversationId })
    );
  }

  markStreamingStop(clientId: string, conversationId: string) {
    this._store.dispatch(
      CopilotActions.stopStreaming({ clientId, conversationId })
    );
  }

  archiveConversation(clientId: string, conversationId: string) {
    this._store.dispatch(
      CopilotActions.archiveConversation({ clientId, conversationId })
    );
  }

  closeConversation(clientId: string, conversationId: string) {
    this._store.dispatch(
      CopilotActions.closeConversation({ clientId, conversationId })
    );
  }

  loadAndSelectConversation(
    clientId: string,
    conversationId: string,
    state: 'active' | 'archived' = 'active',
    forceRefresh = false
  ) {
    this.loadMessages(clientId, conversationId, forceRefresh);
    this._store
      .select(selectMessagesByConversation(clientId, conversationId))
      .pipe(
        filter((messages) => {
          return Array.isArray(messages);
        }),
        take(1)
      )
      .subscribe(() => {
        this.selectConversation(clientId, conversationId, state);
      });
  }

  triggerClientHighlight(clientId: string) {
    this._store.dispatch(CopilotActions.triggerClientHighlight({ clientId }));
  }

  clearClientHighlight(clientId: string) {
    this._store.dispatch(CopilotActions.clearClientHighlight({ clientId }));
  }

  clearConversationHighlight(clientId: string, conversationId: string) {
    this._store.dispatch(
      CopilotActions.clearConversationHighlight({ clientId, conversationId })
    );
  }

  clearContext() {
    this.selectedClientId$.pipe(take(1)).subscribe((clientId) => {
      if (clientId) {
        this.selectConversation(clientId, '');
      }
    });

    this._clearContextSignal.update((v) => v + 1);
  }
}
